import React, { useState } from 'react';
import { Modal, Typography } from '@crisellevaldez/speculo-ui';
import { useNavigate } from 'react-router-dom';
import { Check, Calendar } from 'lucide-react';
import { useClientOwner } from '../../../hooks/useClientOwner';

interface TourCompleteModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Optional contact ID to navigate to the specific contact page */
  contactId?: string | null;
  /** Whether the tour was skipped (shows different content) */
  wasSkipped?: boolean;
}

export const TourCompleteModal: React.FC<TourCompleteModalProps> = ({
  isOpen,
  onClose,
  contactId,
  wasSkipped = false,
}) => {
  const navigate = useNavigate();
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const owner = useClientOwner();
  const strategyCallEmbedUrl = `${owner.strategyMeetingUrl}?embed=true`;

  // Determine if this was a completion without test call (Twilio not configured)
  // wasSkipped=false but no contactId means tour completed early without test call
  const completedWithoutTestCall = !wasSkipped && !contactId;

  // Get appropriate message based on completion type
  const getDescription = () => {
    if (wasSkipped) {
      return "Congratulations! You've successfully completed the onboarding. Feel free to navigate the platform and explore all the features available to you.";
    }
    if (completedWithoutTestCall) {
      return "Congratulations! You've successfully completed the Flow guide. Your phone system is being set up by our team - once complete, you'll be able to make test calls. Feel free to explore the platform!";
    }
    return "Congratulations! You've successfully completed the Flow guide. Your test call has been initiated. After the call completes, you can view the conversation transcript on the Contacts page.";
  };

  // Get appropriate button text and action
  const getButtonConfig = () => {
    if (wasSkipped || completedWithoutTestCall) {
      return {
        text: 'Go to Dashboard',
        action: () => {
          onClose();
          navigate('/dashboard');
        },
      };
    }
    return {
      text: 'View Contact',
      action: () => {
        onClose();
        if (contactId) {
          // Open contact page in a new tab
          window.open(`/contact/${contactId}`, '_blank');
        } else {
          // Open contacts page in a new tab
          window.open('/contacts', '_blank');
        }
      },
    };
  };

  const buttonConfig = getButtonConfig();

  const handleScheduleClick = () => {
    setShowScheduleModal(true);
  };

  const handleCloseScheduleModal = () => {
    setShowScheduleModal(false);
  };

  return (
    <>
      <Modal isOpen={isOpen && !showScheduleModal} onClose={onClose}>
        {/* Empty header for the close button only; closing keeps the user on the current flow */}
        <Modal.Header onClose={onClose}>{null}</Modal.Header>
        <div className="text-center pt-8 pb-8 px-6 rounded-b-lg bg-gradient-to-b from-transparent to-yellow-50 dark:to-yellow-900/10">
          <div className="mb-6">
            <div className="flex items-center justify-center gap-3 mb-5">
              <span className="onboarding-complete-badge w-10 h-10 shrink-0 rounded-full bg-gradient-to-br from-green-400 to-emerald-600 shadow-md shadow-green-500/30 flex items-center justify-center">
                <Check className="onboarding-complete-check w-6 h-6 text-white" strokeWidth={3} />
              </span>
              <h2 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-emerald-600 to-yellow-600 bg-clip-text text-transparent">
                Onboarding Complete!
              </h2>
            </div>
            <p className="text-sm sm:text-base text-gray-900 dark:text-gray-100">
              {getDescription()}
            </p>
          </div>

          <p className="text-sm sm:text-base font-medium text-gray-900 dark:text-gray-100 mb-8">
            Please make sure to schedule the strategy call
            <br />
            with your Account Strategist, {owner.fullName}.
          </p>

          {/* Schedule Strategy Call is the only primary action (onboarding NextButton style).
              It sits on the right on desktop, where users tend to click, and on top on mobile. */}
          <div className="flex flex-col-reverse sm:flex-row items-center justify-center gap-4 sm:gap-6">
            <button
              type="button"
              onClick={buttonConfig.action}
              className="text-sm font-medium text-gray-600 underline underline-offset-4 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
            >
              {buttonConfig.text}
            </button>

            <button
              type="button"
              onClick={handleScheduleClick}
              className="h-12 px-8 inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-white text-lg font-semibold shadow-lg hover:shadow-xl transition-all"
            >
              <Calendar className="w-5 h-5" />
              Schedule Strategy Call
            </button>
          </div>
        </div>
      </Modal>

      {/* Schedule Strategy Call Modal with HubSpot Embed */}
      <Modal isOpen={showScheduleModal} onClose={handleCloseScheduleModal} size="lg">
        <Modal.Header>
          <Typography variant="h3">
            Schedule Your Strategy Call
          </Typography>
        </Modal.Header>
        <Modal.Body className="overflow-hidden">
          <div className="meetings-iframe-container" style={{ minHeight: '600px' }}>
            <iframe
              src={strategyCallEmbedUrl}
              width="100%"
              height="600"
              frameBorder="0"
              title="Schedule Strategy Call"
              style={{ border: 'none' }}
            />
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};
